import { useVirtualizer } from "@tanstack/react-virtual";
import { Icon, Tooltip } from "@ttc3k/trekker";
import { NavArrowDown, NavArrowUp } from "iconoir-react";
import {
  useCallback,
  useMemo,
  useRef,
  type PointerEvent as ReactPointerEvent,
} from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router";
import { css, cx } from "styled-system/css";
import { Box, Flex, styled } from "styled-system/jsx";
import { formatDate, formatPrice, formatRenewalDate } from "shared/utils";
import {
  ATTENDEE_CALENDAR_DATE_PARAM,
  getAttendeeNavigationDates,
} from "shared/utils/calendarAttendeeNavigation";
import { EmptyState } from "shared/components";
import { StorefrontKey } from "apps/storefront/storefrontAppView.config";
import {
  buildDefaultColumnWidthVars,
  cssWidthVar,
  DEFAULT_MIN_COLUMN_WIDTH,
  getGridTemplateColumns,
  LISTING_COLUMNS,
  OrgOverviewEntityType,
  PEOPLE_COLUMNS,
  EVENTS_COLUMNS,
  type OrgOverviewColumn,
  type OrgOverviewColumnKey,
  type OrgOverviewRow,
  type OrgOverviewSortDirection,
} from "./orgOverviewColumns";

/** Outer row box. Deliberately a plain block element (not the grid itself), sized to
 * `max(100% of the visible viewport, the columns' natural content width)`:
 * - `width: "100%"` alone caps the row at the viewport width, so once columns overflow enough to
 *   need horizontal scroll, the scrolled-in columns render past the row's own box — no
 *   background/border-bottom reaches them (they're outside the box, not just clipped).
 * - `width: "fit-content"` alone (shrink-to-fit) fixes that, but then rows narrower than the
 *   viewport (columns don't fill the available width) shrink the box below 100%, leaving the
 *   divider/background short of the container's true edge.
 * `fit-content` + `minWidth: "100%"` gets both: the row is always at least the viewport width,
 * but grows to fit the columns (`TableRowGrid`, nested inside) whenever those are wider. */
const TableRow = styled("div", {
  base: {
    width: "fit-content",
    minW: "100%",
  },
});

const TableRowGrid = styled("div", {
  base: {
    display: "inline-grid",
    alignItems: "center",
    columnGap: "300",
    px: "300",
    minW: "0",
    h: "full",
  },
});

const TableCell = styled("div", {
  base: {
    minW: "0",
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    px: "100",
    fontSize: "sm",
    color: "text.mid",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
});

const statusDotStyle = css({
  display: "inline-block",
  width: "10px",
  height: "10px",
  borderRadius: "9999px",
  border: "none",
  outline: "none",
  padding: 0,
  cursor: "default",
  appearance: "none",
});

/** Precomputed once per status so rendering thousands of rows never calls `css()` at runtime. */
const STATUS_DOT_CLASS: Record<string, string> = {
  APPROVED: cx(statusDotStyle, css({ bg: "bg.success.mid" })),
  NEEDS_APPROVAL: cx(statusDotStyle, css({ bg: "bg.warning.mid" })),
  DRAFT: cx(statusDotStyle, css({ bg: "bg.mid" })),
  UNPUBLISHED: cx(statusDotStyle, css({ bg: "bg.darkest" })),
};
const FALLBACK_STATUS_DOT_CLASS = cx(statusDotStyle, css({ bg: "bg.mid" }));

const contactTagBaseStyle = css({
  display: "inline-flex",
  alignItems: "center",
  minW: "0",
  maxW: "full",
  fontSize: "xs",
  fontWeight: "medium",
  borderRadius: "50",
  px: "150",
  py: "50",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  cursor: "default",
});

/** Distinct, clearly-visible fill per role rather than a single subtle neutral tone (which
 * blended into the white/zebra-striped row background and looked like it had no fill at all).
 * A contact who is both primary and financial gets a blended gradient rather than either solid
 * color alone, so the tag visually communicates it covers both roles at a glance. Text stays a
 * consistent dark color across all three variants rather than tinting per-role, since colored
 * text on top of the (already colored) fills read inconsistently row to row. */
const CONTACT_TAG_CLASS = {
  primary: cx(
    contactTagBaseStyle,
    css({ bg: "bg.accent.blue.lightest", color: "text.dark" }),
  ),
  financial: cx(
    contactTagBaseStyle,
    css({ bg: "bg.success.lightest", color: "text.dark" }),
  ),
  both: cx(
    contactTagBaseStyle,
    css({
      background:
        "linear-gradient(90deg, token(colors.bg.accent.blue.light), token(colors.bg.success.light))",
      color: "text.dark",
    }),
  ),
};

type ContactRole = keyof typeof CONTACT_TAG_CLASS;

/** Uses Trekker's own `Tooltip` (see note on `StatusDotTooltip` below) so hovering a contact
 * tag explains which role(s) — primary, financial, or both — that person holds on the listing. */
const ContactTag = ({
  name,
  role,
  tooltip,
}: {
  name: string;
  role: ContactRole;
  tooltip: string;
}) => (
  <Tooltip
    content={tooltip}
    positioning={{ placement: "top" }}
    contentProps={{
      bg: "white",
      color: "text.dark",
      borderWidth: "1px",
      borderColor: "border.light",
      boxShadow: "md",
    }}
  >
    <span className={CONTACT_TAG_CLASS[role]}>{name}</span>
  </Tooltip>
);

/** Uses Trekker's own `Tooltip` (built on Ark UI's `Tooltip` + `Portal` internally, and already
 * relied on elsewhere in the app, e.g. `PersonHeaderCard`) rather than a hand-rolled hover/portal
 * implementation — that hand-rolled version turned out unreliable to trigger from within the
 * virtualized grid. `contentProps` overrides the tag-like content's default visual with a plain
 * white background per the design. */
const StatusDotTooltip = ({
  label,
  dotClassName,
}: {
  label: string;
  dotClassName: string;
}) => (
  <Tooltip
    content={label}
    positioning={{ placement: "top" }}
    contentProps={{
      bg: "white",
      color: "text.dark",
      borderWidth: "1px",
      borderColor: "border.light",
      boxShadow: "md",
    }}
  >
    <span className={dotClassName} />
  </Tooltip>
);

type OrgOverviewTableProps = {
  columns: OrgOverviewColumn[];
  rows: OrgOverviewRow[];
  entityType: (typeof OrgOverviewEntityType)[keyof typeof OrgOverviewEntityType];
  sortColumn: OrgOverviewColumnKey;
  sortDirection: OrgOverviewSortDirection;
  onSortChange: (column: OrgOverviewColumnKey) => void;
  organizationId: string;
  loading: boolean;
  hasBuiltIndex: boolean;
  /** Closes the Org Overview modal after a listing/person name is clicked, so the
   * destination page is immediately visible rather than hidden behind the modal. */
  onClose: () => void;
};

const ROW_HEIGHT = 44;

/** Combined so widths persist across tab switches even for columns unique to one tab. */
const DEFAULT_COLUMN_WIDTH_VARS = buildDefaultColumnWidthVars([
  LISTING_COLUMNS,
  PEOPLE_COLUMNS,
  EVENTS_COLUMNS,
]);

export const OrgOverviewTable = ({
  columns,
  rows,
  entityType,
  sortColumn,
  sortDirection,
  onSortChange,
  organizationId,
  loading,
  hasBuiltIndex,
  onClose,
}: OrgOverviewTableProps) => {
  const { t } = useTranslation("core", { keyPrefix: "ORG_OVERVIEW" });
  const { t: tCore } = useTranslation("core");
  const navigate = useNavigate();
  const location = useLocation();
  const scrollRef = useRef<HTMLDivElement>(null);
  const gridTemplateColumns = useMemo(
    () => getGridTemplateColumns(columns),
    [columns],
  );

  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: 15,
  });

  // Drag-resize mutates a `--col-{key}` CSS variable directly on the scroll container via the
  // DOM, bypassing React entirely — every row (including virtualized ones) reads its column
  // widths from `var(--col-{key})`, so a resize never triggers a re-render of the (potentially
  // thousands-of-rows) table, keeping the drag perfectly smooth.
  const handleResizeStart = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>, column: OrgOverviewColumn) => {
      event.preventDefault();
      event.stopPropagation();
      const container = scrollRef.current;
      if (!container) return;

      const varName = cssWidthVar(column.key);
      const startX = event.clientX;
      const currentValue =
        getComputedStyle(container).getPropertyValue(varName);
      const startWidth = parseFloat(currentValue) || column.defaultWidth;
      const minWidth = column.minWidth ?? DEFAULT_MIN_COLUMN_WIDTH;

      const handle = event.currentTarget;
      handle.setPointerCapture(event.pointerId);

      const handleMove = (moveEvent: PointerEvent) => {
        const delta = moveEvent.clientX - startX;
        const newWidth = Math.max(minWidth, Math.round(startWidth + delta));
        container.style.setProperty(varName, `${newWidth}px`);
      };

      const handleEnd = () => {
        handle.releasePointerCapture(event.pointerId);
        handle.removeEventListener("pointermove", handleMove);
        handle.removeEventListener("pointerup", handleEnd);
        handle.removeEventListener("pointercancel", handleEnd);
      };

      handle.addEventListener("pointermove", handleMove);
      handle.addEventListener("pointerup", handleEnd);
      handle.addEventListener("pointercancel", handleEnd);
    },
    [],
  );

  const handleRowClick = (row: OrgOverviewRow) => {
    onClose();

    if (entityType === OrgOverviewEntityType.Listing) {
      navigate(`/app/organization/${organizationId}/directory/${row.entityId}`);
      return;
    }

    if (entityType === OrgOverviewEntityType.Event) {
      const eventsPath = `/app/organization/${organizationId}/events`;
      const targetUrl = `${eventsPath}?search=${encodeURIComponent(row.name)}`;
      if (location.pathname === eventsPath) {
        window.location.href = targetUrl;
      } else {
        navigate(targetUrl);
      }
      return;
    }

    const peoplePath = `/app/organization/${organizationId}/people`;
    const targetUrl = `${peoplePath}?search=${encodeURIComponent(row.name)}`;

    // The People app only reads `?search=` once, as the initial value for its search
    // state (see `usePeopleLayout`) — it doesn't watch for URL changes after mount. A
    // soft `navigate()` while already on that route would update the address bar but
    // never re-run the search, so force a full page load in that case to guarantee a
    // fresh mount that picks up the new name.
    if (location.pathname === peoplePath) {
      window.location.href = targetUrl;
    } else {
      navigate(targetUrl);
    }
  };

  const navigateToOrdersSearch = (
    storefrontId: string,
    invoiceNumbers: string[],
  ) => {
    onClose();

    const ordersPath = `/app/organization/${organizationId}/storefront/${storefrontId}`;
    const targetUrl = `${ordersPath}?view=${StorefrontKey.Orders}&search=${encodeURIComponent(invoiceNumbers.join(","))}`;

    // Orders search is read once on mount (see `useOrdersView`), same as People.
    if (location.pathname === ordersPath) {
      window.location.href = targetUrl;
    } else {
      navigate(targetUrl);
    }
  };

  const handlePaidInvoicesClick = (row: OrgOverviewRow) => {
    const storefrontId = row.invoiceStorefrontId;
    const invoiceNumbers = row.paidInvoiceNumbers?.filter(Boolean) ?? [];
    if (!storefrontId || invoiceNumbers.length === 0) return;

    navigateToOrdersSearch(storefrontId, invoiceNumbers);
  };

  const handleUnpaidInvoicesClick = (row: OrgOverviewRow) => {
    const storefrontId = row.unpaidInvoiceStorefrontId;
    const invoiceNumbers = row.unpaidInvoiceNumbers?.filter(Boolean) ?? [];
    if (!storefrontId || invoiceNumbers.length === 0) return;

    navigateToOrdersSearch(storefrontId, invoiceNumbers);
  };

  const handleAttendeesClick = (row: OrgOverviewRow) => {
    const storefrontId = row.attendeeStorefrontId;
    if (!storefrontId || !row.entityId || !(row.attendeeCount ?? 0)) return;

    onClose();
    const calendarPath = `/app/organization/${organizationId}/storefront/${storefrontId}`;
    const { calendarDate, sessionDate } = getAttendeeNavigationDates({
      earliestSessionDate: row.attendeeSessionDate,
      sessionDateCount: row.attendeeSessionDateCount,
      fallbackDate: row.eventStartDate,
    });
    const params = new URLSearchParams({
      view: StorefrontKey.Calendar,
      productType: "EVENT",
      eventAttendees: String(row.entityId),
      sessionDate,
    });
    if (calendarDate) {
      params.set(ATTENDEE_CALENDAR_DATE_PARAM, calendarDate);
    }
    navigate(`${calendarPath}?${params.toString()}`);
  };

  const renderCell = (row: OrgOverviewRow, column: OrgOverviewColumn) => {
    switch (column.key) {
      case "status": {
        if (!row.status) return null;
        return (
          <StatusDotTooltip
            label={tCore(`PUBLICATION_STATUS.${row.status}`, {
              defaultValue: row.status,
            })}
            dotClassName={
              STATUS_DOT_CLASS[row.status] ?? FALLBACK_STATUS_DOT_CLASS
            }
          />
        );
      }
      case "name":
        return (
          <styled.span
            color="text.accent.blue.dark"
            fontWeight="medium"
            cursor="pointer"
            overflow="hidden"
            textOverflow="ellipsis"
            _hover={{ textDecoration: "underline" }}
            onClick={e => {
              e.stopPropagation();
              handleRowClick(row);
            }}
          >
            {row.name}
          </styled.span>
        );
      case "peopleCount": {
        const primary = row.primaryContactName || undefined;
        const financial = row.financialContactName || undefined;

        if (!primary && !financial) {
          const staffNames = row.staffNames?.filter(Boolean) ?? [];
          if (staffNames.length === 0) {
            return row.peopleCount ?? 0;
          }
          const visible = staffNames.slice(0, 2);
          const overflow = staffNames.length - visible.length;
          return (
            <Flex
              align="center"
              gap="75"
              overflow="hidden"
              minW="0"
              whiteSpace="nowrap"
            >
              <styled.span
                color="text.mid"
                overflow="hidden"
                textOverflow="ellipsis"
                minW="0"
              >
                {visible.join(", ")}
              </styled.span>
              {overflow > 0 && (
                <styled.span color="text.light" flexShrink={0}>
                  {t("ADDITIONAL_STAFF", { count: overflow })}
                </styled.span>
              )}
            </Flex>
          );
        }

        const sameContact = Boolean(
          primary && financial && primary === financial,
        );
        const namedCount = sameContact
          ? 1
          : (primary ? 1 : 0) + (financial ? 1 : 0);
        const additionalCount = Math.max(
          0,
          (row.peopleCount ?? 0) - namedCount,
        );

        return (
          <Flex
            align="center"
            gap="75"
            overflow="hidden"
            minW="0"
            whiteSpace="nowrap"
          >
            {sameContact ? (
              <ContactTag
                name={primary as string}
                role="both"
                tooltip={t("CONTACT_BADGE.BOTH_TOOLTIP")}
              />
            ) : (
              <>
                {primary && (
                  <ContactTag
                    name={primary}
                    role="primary"
                    tooltip={t("CONTACT_BADGE.PRIMARY_TOOLTIP")}
                  />
                )}
                {financial && (
                  <ContactTag
                    name={financial}
                    role="financial"
                    tooltip={t("CONTACT_BADGE.FINANCIAL_TOOLTIP")}
                  />
                )}
              </>
            )}
            {additionalCount > 0 && (
              <styled.span color="text.light" flexShrink={0}>
                {t("ADDITIONAL_STAFF", { count: additionalCount })}
              </styled.span>
            )}
          </Flex>
        );
      }
      case "paidInvoices": {
        const label = `$${formatPrice(row.paidInvoiceTotal)} (${row.paidInvoiceCount})`;
        const canOpenOrders =
          (row.paidInvoiceCount ?? 0) > 0 &&
          !!row.invoiceStorefrontId &&
          (row.paidInvoiceNumbers?.length ?? 0) > 0;

        if (!canOpenOrders) return label;

        return (
          <styled.span
            color="text.accent.blue.dark"
            fontWeight="medium"
            cursor="pointer"
            overflow="hidden"
            textOverflow="ellipsis"
            _hover={{ textDecoration: "underline" }}
            onClick={e => {
              e.stopPropagation();
              handlePaidInvoicesClick(row);
            }}
          >
            {label}
          </styled.span>
        );
      }
      case "unpaidInvoices": {
        const label = `$${formatPrice(row.unpaidInvoiceTotal)} (${row.unpaidInvoiceCount})`;
        const canOpenOrders =
          (row.unpaidInvoiceCount ?? 0) > 0 &&
          !!row.unpaidInvoiceStorefrontId &&
          (row.unpaidInvoiceNumbers?.length ?? 0) > 0;

        if (!canOpenOrders) return label;

        return (
          <styled.span
            color="text.accent.blue.dark"
            fontWeight="medium"
            cursor="pointer"
            overflow="hidden"
            textOverflow="ellipsis"
            _hover={{ textDecoration: "underline" }}
            onClick={e => {
              e.stopPropagation();
              handleUnpaidInvoicesClick(row);
            }}
          >
            {label}
          </styled.span>
        );
      }
      case "membershipTierName":
        return row.membershipTierName ?? "—";
      case "membershipStatusNames":
        return row.membershipStatusNames?.length
          ? row.membershipStatusNames.join(", ")
          : "—";
      case "membershipRenewalDate":
        // Calendar-day-only value — see `formatRenewalDate`'s doc comment.
        return row.membershipRenewalDate
          ? formatRenewalDate(row.membershipRenewalDate)
          : "—";
      case "noteCount":
        return row.noteCount ?? 0;
      case "eventCount":
        return row.eventCount ?? 0;
      case "eventStartDate":
        return row.eventStartDate ? formatDate(row.eventStartDate) : "—";
      case "eventEndDate":
        return row.eventEndDate ? formatDate(row.eventEndDate) : "—";
      case "recurrenceType":
        return row.recurrenceType
          ? t(`RECURRENCE_TYPE.${row.recurrenceType}`, {
              defaultValue: row.recurrenceType,
            })
          : "—";
      case "invoices": {
        const totalAmount =
          (row.paidInvoiceTotal ?? 0) + (row.unpaidInvoiceTotal ?? 0);
        const totalCount =
          (row.paidInvoiceCount ?? 0) + (row.unpaidInvoiceCount ?? 0);
        const label = `$${formatPrice(totalAmount)} (${totalCount})`;
        const canOpenOrders =
          totalCount > 0 &&
          !!row.invoiceStorefrontId &&
          (row.paidInvoiceNumbers?.length ?? 0) > 0;

        if (!canOpenOrders) return label;

        return (
          <styled.span
            color="text.accent.blue.dark"
            fontWeight="medium"
            cursor="pointer"
            overflow="hidden"
            textOverflow="ellipsis"
            _hover={{ textDecoration: "underline" }}
            onClick={e => {
              e.stopPropagation();
              handlePaidInvoicesClick(row);
            }}
          >
            {label}
          </styled.span>
        );
      }
      case "attendeeCount": {
        const count = row.attendeeCount ?? 0;
        if (!count || !row.attendeeStorefrontId) return count;
        return (
          <styled.span
            color="text.accent.blue.dark"
            fontWeight="medium"
            cursor="pointer"
            _hover={{ textDecoration: "underline" }}
            onClick={event => {
              event.stopPropagation();
              handleAttendeesClick(row);
            }}
          >
            {count}
          </styled.span>
        );
      }
      case "customTagNames":
        return row.customTagNames?.length ? row.customTagNames.join(", ") : "—";
      case "primaryTag":
        return row.primaryTag
          ? tCore(`LOOKUPS.OPERATOR_TYPE.${row.primaryTag}`, {
              defaultValue: row.primaryTag,
            })
          : "—";
      default:
        return null;
    }
  };

  if (!hasBuiltIndex && !loading) {
    return (
      <EmptyState
        text={t("EMPTY_STATE.NOT_BUILT")}
        h="full"
        minH="300px"
        flex="1"
      />
    );
  }

  if (!loading && rows.length === 0) {
    return (
      <EmptyState
        text={t("EMPTY_STATE.NO_RESULTS")}
        h="full"
        minH="300px"
        flex="1"
      />
    );
  }

  return (
    <Box
      ref={scrollRef}
      flex="1"
      minH="0"
      overflow="auto"
      style={DEFAULT_COLUMN_WIDTH_VARS as React.CSSProperties}
    >
      <TableRow
        borderBottomWidth="1px"
        borderBottomColor="border.mid"
        position="sticky"
        top="0"
        zIndex="1"
        bg="white"
      >
        <TableRowGrid style={{ gridTemplateColumns }} py="200">
          {columns.map(column => (
            <TableCell
              key={column.key}
              overflow="visible"
              position="relative"
              justifyContent={
                column.align === "right" ? "flex-end" : "flex-start"
              }
              fontWeight="semibold"
              color="text.dark"
              cursor={column.sortable ? "pointer" : "default"}
              userSelect="none"
              onClick={() => column.sortable && onSortChange(column.key)}
            >
              <Flex align="center" gap="50" overflow="hidden" minW="0">
                <styled.span
                  overflow="hidden"
                  textOverflow="ellipsis"
                  whiteSpace="nowrap"
                >
                  {t(`COLUMNS.${column.labelKey}`)}
                </styled.span>
                {column.sortable && sortColumn === column.key && (
                  <Icon
                    Element={
                      sortDirection === "asc" ? NavArrowUp : NavArrowDown
                    }
                    size="xs"
                  />
                )}
              </Flex>

              <Box
                position="absolute"
                top="0"
                bottom="0"
                right="-12px"
                w="16px"
                display="flex"
                justifyContent="center"
                cursor="col-resize"
                zIndex="2"
                onPointerDown={e => handleResizeStart(e, column)}
                onClick={e => e.stopPropagation()}
              >
                <Box
                  w="2px"
                  h="full"
                  bg="border.light"
                  transitionProperty="background"
                  transitionDuration="fast"
                  _hover={{ bg: "text.accent.blue.dark" }}
                />
              </Box>
            </TableCell>
          ))}
        </TableRowGrid>
      </TableRow>

      <Box
        position="relative"
        width="100%"
        height={`${virtualizer.getTotalSize()}px`}
      >
        {virtualizer.getVirtualItems().map(virtualRow => {
          const row = rows[virtualRow.index];
          return (
            <TableRow
              key={row._id}
              position="absolute"
              top="0"
              left="0"
              height={`${virtualRow.size}px`}
              style={{ transform: `translateY(${virtualRow.start}px)` }}
              borderBottomWidth="1px"
              borderBottomColor="border.light"
              bg={virtualRow.index % 2 === 1 ? "bg.wa.lightest" : "white"}
            >
              <TableRowGrid style={{ gridTemplateColumns }}>
                {columns.map(column => (
                  <TableCell
                    key={column.key}
                    overflow={column.key === "status" ? "visible" : "hidden"}
                    justifyContent={
                      column.align === "right" ? "flex-end" : "flex-start"
                    }
                  >
                    {renderCell(row, column)}
                  </TableCell>
                ))}
              </TableRowGrid>
            </TableRow>
          );
        })}
      </Box>
    </Box>
  );
};
